import ChevronDown20 from '@carbon/icons-react/es/chevron--down/20';
import { Dropdown } from 'antd';
import React, { FC, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { ReactComponent as ApproveOutline16 } from 'assets/svgs/approve-outline.svg';
import { ReactComponent as RejectIcon } from 'assets/svgs/reject_button_icon.svg';
import { Button } from 'components/atoms/button';
import { Menu } from 'components/atoms/dropdown';
import { RejectTaskNoteForm } from '../approval-user-view/reject-note-form/reject-note-form';
import {
  TimesheetStatus,
  TimesheetWeeklyRow,
  TimesheetWeeklyRowTask,
} from '../types';

const ButtonGroup = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  & button:not(:first-child) {
    margin-left: 1px;
  }
`;

const PrimaryTagStyled = styled(Button)`
  width: 72%;
  cursor: default;
  padding-right: 0;
`;

const PrimaryButtonStyled = styled(Button)`
  width: 72%;
`;

const MenuStyled = styled(Menu)`
  width: 160px;
  padding: var(--spacing-03) 0;

  .ant-dropdown-menu-submenu-expand-icon {
    display: none;
  }
`;

const MenuItemStyled = styled(Menu.Item)`
  padding: 0 var(--spacing-03);
`;

const SubMenu = styled(Menu.SubMenu)`
  #reject-box {
    padding: 0 var(--spacing-03);
  }

  .ant-dropdown-menu-submenu {
    width: auto !important;
  }
`;

export const NON_PROJECT_ACTIVITY_ID = -1;

const ApprovedState = ({ isWeekFreezed, isUserAdmin, menu }) => (
  <ButtonGroup>
    <PrimaryTagStyled
      size="small"
      type="success"
      icon={<ApproveOutline16 />}
      iconPlacement="left"
    >
      <FormattedMessage id="timesheets.view_types.approved" />
    </PrimaryTagStyled>

    {(!isWeekFreezed || isUserAdmin) && (
      <Dropdown overlay={menu} trigger={['click']}>
        <Button size="small" type="success" icon={<ChevronDown20 />} />
      </Dropdown>
    )}
  </ButtonGroup>
);

const SubmittedState = ({
  approveTask,
  record,
  menu,
  handleOpenChange,
  open,
}) => (
  <ButtonGroup>
    <PrimaryButtonStyled
      size="small"
      type="tertiary"
      onClick={() =>
        approveTask(
          record.task,
          record.task.project?.id ?? NON_PROJECT_ACTIVITY_ID,
        )
      }
    >
      <FormattedMessage id="approve" />
    </PrimaryButtonStyled>

    <Dropdown
      overlay={menu}
      trigger={['click']}
      onVisibleChange={handleOpenChange}
      visible={open}
    >
      <Button size="small" type="tertiary" icon={<ChevronDown20 />} />
    </Dropdown>
  </ButtonGroup>
);

const RejectedState = ({ isWeekFreezed, isUserAdmin, menu }) => (
  <ButtonGroup>
    <PrimaryTagStyled
      size="small"
      type="primary"
      danger
      icon={<RejectIcon />}
      iconPlacement="left"
    >
      <FormattedMessage id="timesheets.feature.rejected" />
    </PrimaryTagStyled>

    {(!isWeekFreezed || isUserAdmin) && (
      <Dropdown overlay={menu} trigger={['click']}>
        <Button size="small" type="primary" danger icon={<ChevronDown20 />} />
      </Dropdown>
    )}
  </ButtonGroup>
);

export const ActionBtnCell: FC<{
  record: TimesheetWeeklyRow;
  approveTask: (task: TimesheetWeeklyRowTask, projectId?: number) => void;
  rejectTask: (
    task: TimesheetWeeklyRowTask,
    content?: string,
    projectId?: number,
  ) => Promise<any>;
  withdrawTask: (task: TimesheetWeeklyRowTask, projectId?: number) => void;
  isWeekFreezed: boolean;
  isUserAdmin: boolean;
}> = ({
  record,
  approveTask,
  rejectTask,
  withdrawTask,
  isWeekFreezed,
  isUserAdmin,
}) => {
  const { approvalStatus } = record.task;
  const [open, setOpen] = useState(false);
  const [visible, setVisible] = useState(false);

  const handleOpenChange = (flag: boolean) => {
    setOpen(flag);
  };

  const onOpenChange = (keys: React.Key[]) => {
    if (!keys.find((key) => key === 'reject-box')) {
      setVisible(false);
    }
  };

  const menu = (
    <MenuStyled onOpenChange={onOpenChange}>
      {approvalStatus === TimesheetStatus.Approved ||
      approvalStatus === TimesheetStatus.Submitted ? (
        <SubMenu
          key="reject-box"
          icon={<div />}
          title={<FormattedMessage id="timesheets.feature.reject" />}
          onTitleClick={() => setVisible(true)}
        >
          {visible ? (
            <RejectTaskNoteForm
              onReject={rejectTask}
              record={record}
              closePopup={() => setVisible(false)}
            />
          ) : null}
        </SubMenu>
      ) : null}
      {approvalStatus === TimesheetStatus.Rejected ? (
        <MenuItemStyled
          key={TimesheetStatus.Approved}
          onClick={() =>
            approveTask(
              record.task,
              record.task.project?.id ?? NON_PROJECT_ACTIVITY_ID,
            )
          }
        >
          <FormattedMessage id="approve" />
        </MenuItemStyled>
      ) : null}
      {approvalStatus === TimesheetStatus.Approved ? (
        <MenuItemStyled
          key={TimesheetStatus.Submitted}
          onClick={() =>
            withdrawTask(
              record.task,
              record.task.project?.id ?? NON_PROJECT_ACTIVITY_ID,
            )
          }
        >
          <FormattedMessage id="timesheets.buttons.withdraw" />
        </MenuItemStyled>
      ) : null}
    </MenuStyled>
  );

  if (approvalStatus === TimesheetStatus.Approved) {
    return (
      <ApprovedState
        isWeekFreezed={isWeekFreezed}
        isUserAdmin={isUserAdmin}
        menu={menu}
      />
    );
  }

  if (approvalStatus === TimesheetStatus.Rejected) {
    return (
      <RejectedState
        isWeekFreezed={isWeekFreezed}
        isUserAdmin={isUserAdmin}
        menu={menu}
      />
    );
  }

  if (approvalStatus === TimesheetStatus.Submitted) {
    if (!isWeekFreezed || isUserAdmin) {
      return (
        <SubmittedState
          approveTask={approveTask}
          record={record}
          handleOpenChange={handleOpenChange}
          open={open}
          menu={menu}
        />
      );
    }
  }

  return null;
};
